import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon, CircleCheckIcon, CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ReactNode, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { ReportSnapshotMatches } from 'dashboard/components/ReportHistoryTimeline';
import {
  AdverseMediaIgnoreHistoryDetails,
  PoliticallyExposedPersonIgnoreHistoryDetails,
  WatchlistIgnoreHistoryDetails,
} from 'dashboard/components/ReportHistoryTimeline/ReportIgnoreHistory';
import Banner from 'dashboard/components-lib/Banner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Report, ReportRecurStatus, SNAPSHOT_VIEW_REPORT_TYPES } from 'dashboard/models/report';
import { AdverseMediaIgnoreListEntry } from 'dashboard/models/report/adverse-media';
import { PoliticallyExposedPersonIgnoreListEntry } from 'dashboard/models/report/politically-exposed-person';
import {
  WatchlistIgnoreEntityEntry,
  WatchlistIgnoreListEntry,
  WatchlistMatch,
} from 'dashboard/models/report/watchlist';
import {
  ActionType,
  EntryType,
  ReportHistory,
  ReportHistoryTimelineEntry,
} from 'dashboard/models/report-history';
import { ReportRunCustomListHitEntry } from 'dashboard/models/report-run';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import ImageDot from 'images/dashboard/dot.svg';
import { DATE_AND_TIME_FORMAT, DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

interface ReportHistoryProps {
  report: Report;
  linkToRuns?: boolean;
}

const ActionTextMap = {
  [ActionType.ReportResumed]: 'Continuous Monitoring Resumed',
  [ActionType.ReportPaused]: 'Continuous Monitoring Paused',
};

enum View {
  TIMELINE = 1,
  SNAPSHOT = 2,
  IGNORE = 3,
}

interface ViewClickParameters {
  entries?:
    | WatchlistIgnoreListEntry[]
    | WatchlistIgnoreEntityEntry[]
    | AdverseMediaIgnoreListEntry[]
    | AdverseMediaIgnoreListEntry
    | ReportRunCustomListHitEntry
    | PoliticallyExposedPersonIgnoreListEntry[];
  snapshotId?: string;
  snapshotToken?: string;
  runId?: string;
}
interface HistoryEntryProps {
  entry: ReportHistoryTimelineEntry;
  recurStatus?: string;
  snapshotViewEnabled: boolean;
  linkToRuns: boolean;
  onViewClick: ({ entries, snapshotId }: ViewClickParameters) => void;
  reportType: Report['type'];
}

export const ReportHistoryTimeline = ({ report, linkToRuns = false }: ReportHistoryProps) => {
  const [timeline, setTimeline] = useState<ReportHistoryTimelineEntry[] | null>(null);
  const [isErrored, setIsErrored] = useState(false);
  const [snapshotMatches, setSnapshotMatches] = useState<Record<string, WatchlistMatch[]> | null>(
    null,
  );
  const snapshotViewEnabled = SNAPSHOT_VIEW_REPORT_TYPES.includes(report.type);
  const [ignoreListEntries, setIgnoreListEntries] = useState<
    | WatchlistIgnoreListEntry[]
    | WatchlistIgnoreEntityEntry[]
    | AdverseMediaIgnoreListEntry[]
    | AdverseMediaIgnoreListEntry
    | ReportRunCustomListHitEntry
    | PoliticallyExposedPersonIgnoreListEntry[]
    | null
  >(null);
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<View>(View.TIMELINE);

  useEffect(() => {
    SessionClientDashboard.get<ReportHistory>(ApiEndpoint.Report, report.id, 'history')
      .then((response) => {
        setTimeline(response.timeline);
        setSnapshotMatches(response['snapshot-matches']);
      })
      .catch(() => {
        setIsErrored(true);
      });
  }, [report]);

  if (report.attributes['redacted-at']) {
    return <Banner icon={<CircleAlertIcon />}>This report has been redacted</Banner>;
  }

  const handleViewClick = ({ runId, entries, snapshotId, snapshotToken }: ViewClickParameters) => {
    if (runId && linkToRuns) {
      window.open(`/dashboard/reports/${report.id}/runs/${runId}?isActive=0`);
      return;
    } else if (snapshotToken) {
      // Stay on the same modal but open a new tab for the additional info snapshot
      window.open(`/dashboard/report-snapshot/${snapshotToken}`);
      return;
    } else if (entries) {
      setIgnoreListEntries(entries);
      setCurrentView(View.IGNORE);
      return;
    } else if (snapshotId) {
      setSnapshotId(snapshotId);
      setCurrentView(View.SNAPSHOT);
      return;
    }
  };

  if (isErrored) {
    return (
      <ErrorBody>
        <ErrorIcon />
        <Paragraph color='tertiary'>Error loading report history.</Paragraph>
      </ErrorBody>
    );
  } else {
    switch (currentView) {
      case View.IGNORE: {
        switch (report.type) {
          case ApiModel.ReportAdverseMedia:
            return (
              <AdverseMediaIgnoreHistoryDetails
                entries={ignoreListEntries as AdverseMediaIgnoreListEntry[]}
                onBackClick={() => {
                  setIgnoreListEntries(null);
                  setCurrentView(View.TIMELINE);
                }}
              />
            );
          case ApiModel.ReportBusinessAdverseMedia:
            return (
              <AdverseMediaIgnoreHistoryDetails
                entries={ignoreListEntries as AdverseMediaIgnoreListEntry[]}
                onBackClick={() => {
                  setIgnoreListEntries(null);
                  setCurrentView(View.TIMELINE);
                }}
              />
            );
          case ApiModel.ReportBusinessWatchlist:
            return (
              <WatchlistIgnoreHistoryDetails
                entries={ignoreListEntries as WatchlistIgnoreListEntry[]}
                onBackClick={() => {
                  setIgnoreListEntries(null);
                  setCurrentView(View.TIMELINE);
                }}
              />
            );
          case ApiModel.ReportWatchlist:
            return (
              <WatchlistIgnoreHistoryDetails
                entries={ignoreListEntries as WatchlistIgnoreListEntry[]}
                onBackClick={() => {
                  setIgnoreListEntries(null);
                  setCurrentView(View.TIMELINE);
                }}
              />
            );
          case ApiModel.ReportPoliticallyExposedPerson:
            return (
              <PoliticallyExposedPersonIgnoreHistoryDetails
                entries={ignoreListEntries as PoliticallyExposedPersonIgnoreListEntry[]}
                onBackClick={() => {
                  setIgnoreListEntries(null);
                  setCurrentView(View.TIMELINE);
                }}
              />
            );
          default: // Should never get to this case
            return <div>No ignore hits found</div>;
        }
      }
      case View.SNAPSHOT: {
        return (
          <>
            {snapshotMatches && snapshotId && (
              <ReportSnapshotMatches
                reportType={report.type}
                entries={snapshotMatches[snapshotId]}
                onBackClick={() => {
                  setSnapshotId(null);
                  setCurrentView(View.TIMELINE);
                }}
              />
            )}
          </>
        );
      }
      case View.TIMELINE:
      default:
        return (
          <div>
            {timeline ? (
              timeline.map((entry: ReportHistoryTimelineEntry) => (
                <HistoryEntry
                  key={entry.timestamp}
                  entry={entry}
                  recurStatus={report.attributes['recur-status']}
                  snapshotViewEnabled={snapshotViewEnabled}
                  linkToRuns={linkToRuns}
                  onViewClick={handleViewClick}
                  reportType={report.type}
                />
              ))
            ) : (
              <LoadingIndicator />
            )}
          </div>
        );
    }
  }
};

const HistoryEntry = ({
  entry,
  recurStatus,
  snapshotViewEnabled,
  onViewClick,
  reportType,
}: HistoryEntryProps) => {
  // TODO: refactor this naming. Timestamp is somewhat deceiving since it's not a timestamp but rather
  // an ISO String. This causes problems with our new date package, but not with moment, since it takes both
  const timestamp = `${DateTime.fromISO(entry.timestamp).toFormat(DATE_AND_TIME_TZ_FORMAT)}`;
  switch (entry.type) {
    case EntryType.Dismissal: {
      // If this is a watchlist entity, disallow clicking since the component doesn't support it
      // TODO: refactor in this in the future
      return (
        <HistoryEntryComponent
          entryType={entry.type}
          completedAt={entry['completed-at']}
          matches={entry.matches}
          title='Dismissed Matches'
          subtitle={getDismissLabel(entry)}
          tooltipContent={
            <TooltipWrapper>
              {entry['ignore-list-custom-list-entity'] && (
                <Paragraph>{`Matched from row ${
                  entry['ignore-list-custom-list-entity']['row-number'] ||
                  entry['ignore-list-custom-list-entity']['comparison-detail']['row-number']
                }`}</Paragraph>
              )}
              <Paragraph>{`Notes: ${getIgnoreReason(entry)}`}</Paragraph>
              <Paragraph>{`Reviewer: ${entry['creator-name']}`}</Paragraph>
            </TooltipWrapper>
          }
          timestamp={timestamp}
          onEntryClick={() => {
            onViewClick({
              runId: entry['report-run-id'],
              entries: getIgnoreEntries(entry) ?? [],
            });
          }}
          reportType={reportType}
        />
      );
    }
    case EntryType.ReportAction:
      return (
        <HistoryEntryComponent
          entryType={entry.type}
          completedAt={entry['completed-at']}
          title={ActionTextMap[entry['action-type'] as keyof typeof ActionTextMap]}
          subtitle={`By ${entry['creator-name']}`}
          timestamp={timestamp}
          reportType={reportType}
        />
      );
    case EntryType.ReportRun: /* Make report run the default case, since it's the most common */
    default: {
      if (entry['completed-at'] != null) {
        return (
          <HistoryEntryComponent
            entryType={entry.type}
            completedAt={entry['completed-at']}
            matches={entry.matches}
            title='Report Run'
            subtitle={
              reportType === ApiModel.ReportBusinessNonprofit
                ? getResultsLabel(entry.matches)
                : getMatchesLabel(entry.matches)
            }
            tooltipContent={
              <TooltipWrapper>
                <Paragraph>{`Scheduled: ${DateTime.fromISO(entry.timestamp).toFormat(
                  DATE_AND_TIME_TZ_FORMAT,
                )}`}</Paragraph>
                <Paragraph>{`Completed: ${DateTime.fromISO(entry['completed-at']).toFormat(
                  DATE_AND_TIME_TZ_FORMAT,
                )}`}</Paragraph>
                {entry['run-type'] && (
                  <Paragraph>
                    {entry['run-type'] === 'manual'
                      ? `By ${entry['creator-name']}`
                      : 'Scheduled run'}
                  </Paragraph>
                )}
              </TooltipWrapper>
            }
            timestamp={timestamp}
            onEntryClick={
              (snapshotViewEnabled || entry['report-run-id']) && entry.matches && entry.matches > 0
                ? () => {
                    onViewClick({
                      runId: entry['report-run-id'],
                      snapshotId: entry['report-snapshot-id'],
                      snapshotToken: entry['report-snapshot-token'],
                    });
                  }
                : undefined
            }
            reportType={reportType}
          />
        );
      } else {
        return (
          <HistoryEntryComponent
            entryType={entry.type}
            completedAt={entry['completed-at']}
            title={`Scheduled Run${recurStatus === ReportRecurStatus.Paused ? ' - Paused' : ''}`}
            subtitle={`at ${DateTime.fromISO(entry.timestamp).toFormat(DATE_AND_TIME_TZ_FORMAT)}`}
            timestamp='-'
            reportType={reportType}
          />
        );
      }
    }
  }
};

interface HistoryEntryComponentProps {
  entryType: string;
  completedAt?: string;
  matches?: number;
  title: string;
  tooltipContent?: string | ReactNode;
  timestamp: string;
  subtitle?: string;
  onEntryClick?: () => void;
  reportType: Report['type'];
}

const HistoryEntryComponent = ({
  entryType,
  completedAt,
  matches,
  title,
  tooltipContent,
  timestamp,
  subtitle,
  onEntryClick,
  reportType,
}: HistoryEntryComponentProps) => {
  return (
    <HistoryEntryContainer>
      <HistoryEntryIconContainer>
        {entryType !== EntryType.ReportRun || completedAt == null || matches == null ? (
          <HistoryEntryDot>
            <ImageDot height={12} width={12} />
          </HistoryEntryDot>
        ) : (
          <HistoryEntryIcon>
            {matches === 0 || reportType === ApiModel.ReportBusinessNonprofit ? (
              <CircleCheckIcon size={24} />
            ) : (
              <StyledAlertIcon />
            )}
          </HistoryEntryIcon>
        )}
      </HistoryEntryIconContainer>
      <HistoryEntryDetailsContainer>
        <HistoryEntryRow>
          <FlexDiv>
            <Paragraph color='primary'>{title}</Paragraph>
            {tooltipContent && (
              <Tooltip portal={false} iconTrigger={<InfoIcon />} side='bottom'>
                {tooltipContent}
              </Tooltip>
            )}
          </FlexDiv>
          <Paragraph>{timestamp}</Paragraph>
        </HistoryEntryRow>
        <HistoryEntryRow>
          <Paragraph>{subtitle}</Paragraph>
          {onEntryClick && (
            <ViewButton width='auto' id='report-history__view-button' onClick={onEntryClick}>
              View
            </ViewButton>
          )}
        </HistoryEntryRow>
      </HistoryEntryDetailsContainer>
    </HistoryEntryContainer>
  );
};

const ErrorBody = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
`;

const ErrorIcon = styled(CircleAlertIcon).attrs({ color: Color.BorderIconDanger })`
  width: ${Spacing[7]};
  height: ${Spacing[7]};
  margin: ${Spacing[2]};
  > * {
    stroke: ${Color.CoreSlate700};
    stroke-width: 2;
  }
`;

const InfoIcon = styled(CircleInfoIcon)`
  margin: ${Spacing[1]};
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const StyledAlertIcon = styled(CircleAlertIcon).attrs({ color: Color.BorderIconDanger })`
  width: ${Spacing[5]};
  height: ${Spacing[5]};
`;

const HistoryEntryIconContainer = styled('div')`
  margin-right: ${Spacing[2]};
  position: relative;
`;
const IconWidth = 24;
const CommonIconStyles = css`
  width: ${IconWidth}px;
  display: inline-block;
  position: relative;
  border-radius: 50%;
  z-index: 1;
  svg {
    display: block; // removes space for decenders that svg's default inline has
    margin: auto; // center svg as a block element in parent component
  }
`;
const HistoryEntryIcon = styled('div')`
  ${CommonIconStyles};
  background-color: ${({ theme }) => theme.color.background.primary};
  color: ${Color.CoreGreen400};
`;
const HistoryEntryDot = styled('div')`
  ${CommonIconStyles};
  color: ${({ theme }) => theme.color.border.secondary};
`;

const ViewButton = styled(Button)`
  display: none;
`;

const HistoryEntryContainer = styled('div')`
  display: flex;
  justify-items: center;
  padding: ${Spacing[2]} ${Spacing[3]} ${Spacing[3]} ${Spacing[4]};
  border-radius: ${Spacing[1]};
  &:hover {
    background-color: ${({ theme }) => theme.color.background.highlight};
    ${ViewButton} {
      display: block;
    }
    ${HistoryEntryIcon} {
      background-color: ${({ theme }) => theme.color.background.highlight};
    }
  }

  &:not(:last-child) {
    ${HistoryEntryIconContainer}:after {
      content: '';
      position: absolute;
      top: ${Spacing[2]};
      height: 100%;
      border-left: 4px solid ${({ theme }) => theme.color.border.secondary};
      border-bottom-left-radius: 1px;
      border-bottom-right-radius: 1px;
      padding-bottom: ${Spacing[5]};
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const HistoryEntryDetailsContainer = styled('div')`
  width: 100%;
  min-height: 58px;
`;
const HistoryEntryRow = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const FlexDiv = styled('div')`
  display: flex;
`;

const TooltipWrapper = styled.span`
  text-align: left;
`;

const getMatchesLabel = (matches?: number): string | undefined => {
  if (matches == null) {
    return undefined;
  }

  if (matches === 1) {
    return '1 match found.';
  }

  return `${matches} matches found`;
};

const getResultsLabel = (matches?: number): string | undefined => {
  if (matches == null) {
    return undefined;
  }

  if (matches === 1) {
    return '1 result found.';
  }

  return `${matches} results found`;
};
// TODO: refactor everything below to be based on report type instead of keys
const getDismissLabel = (entry: ReportHistoryTimelineEntry): string => {
  const foundKey = ALL_IGNORE_KEYS.find((key) => entry[key] != null);

  if (!foundKey) {
    return '';
  }

  let numDismissed = 0;
  let dismissObjectName = '';
  let reference = '';

  switch (foundKey) {
    case IgnoreKeys.WatchlistLegacy:
    case IgnoreKeys.Pep:
      numDismissed = entry?.[foundKey]?.length || 0;
      dismissObjectName = numDismissed === 1 ? 'match' : 'matches';
      break;
    case IgnoreKeys.WatchlistEntity:
      numDismissed = 1;
      dismissObjectName = 'entity';
      break;
    case IgnoreKeys.CustomListEntity:
      numDismissed = 1;
      dismissObjectName = 'entity';
      if (!isEmpty(entry['report-run-scheduled-at'])) {
        reference = `from run on ${DateTime.fromISO(
          entry['report-run-scheduled-at'] as Iso8601DateString,
        ).toFormat(DATE_AND_TIME_FORMAT)}`;
      }
      break;
    case IgnoreKeys.AdverseMediaEntity:
      numDismissed = entry?.[IgnoreKeys.AdverseMediaEntity]?.match?.['media']?.length || 0;
      dismissObjectName = numDismissed === 1 ? 'entity' : 'entities';
      break;
    case IgnoreKeys.AdverseMediaMedia:
      numDismissed = entry?.['ignore-list-media']?.match?.['media']?.length || 0;
      dismissObjectName = 'media';
      break;
    default:
      numDismissed = 0;
      dismissObjectName = 'matches';
  }

  return `${numDismissed} ${dismissObjectName} dismissed ${reference}`;
};

const getIgnoreReason = (entry: ReportHistoryTimelineEntry): string => {
  const foundKey = ALL_IGNORE_KEYS.find((key) => entry[key] != null);

  if (!foundKey) {
    return '';
  }

  switch (foundKey) {
    case IgnoreKeys.CustomListEntity:
      return entry?.[foundKey]?.dismissal?.reason || '';
    case IgnoreKeys.WatchlistLegacy:
    case IgnoreKeys.Pep:
      return entry?.[foundKey]?.[0]?.['ignore-reason'] || '';
    case IgnoreKeys.WatchlistEntity:
    case IgnoreKeys.AdverseMediaEntity:
    case IgnoreKeys.AdverseMediaMedia:
      return entry?.[foundKey]?.['ignore-reason'] || '';
    default:
      return '';
  }
};

const getIgnoreEntries = (entry: ReportHistoryTimelineEntry) => {
  const foundKey = ALL_IGNORE_KEYS.find((key) => entry[key] != null);

  if (!foundKey || !entry) {
    return [];
  }

  switch (foundKey) {
    case IgnoreKeys.WatchlistLegacy:
    case IgnoreKeys.Pep:
    case IgnoreKeys.AdverseMediaEntity:
    case IgnoreKeys.CustomListEntity:
      return entry?.[foundKey] ?? [];
    case IgnoreKeys.AdverseMediaMedia:
      return [entry?.[foundKey]].filter((entry) => !!entry) as AdverseMediaIgnoreListEntry[];
    case IgnoreKeys.WatchlistEntity:
      return entry?.[foundKey]?.match?.['ignore-list'] ?? [];
    default:
      return [];
  }
};

enum IgnoreKeys {
  CustomListEntity = 'ignore-list-custom-list-entity',
  WatchlistLegacy = 'ignore-list',
  WatchlistEntity = 'ignore-list-watchlist-entity',
  AdverseMediaEntity = 'ignore-list-entity',
  AdverseMediaMedia = 'ignore-list-media',
  Pep = 'ignore-list-pep',
}

const ALL_IGNORE_KEYS = [
  IgnoreKeys.CustomListEntity,
  IgnoreKeys.WatchlistLegacy,
  IgnoreKeys.WatchlistEntity,
  IgnoreKeys.AdverseMediaEntity,
  IgnoreKeys.AdverseMediaMedia,
  IgnoreKeys.Pep,
];
